import type { MouseEvent as ReactMouseEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Arrow } from "@/components/icons";
import { routeHead } from "@/components/seo";
import { organizationSchema } from "@/lib/schema";
import { CountUp } from "@/components/count-up";
import { CoverageMap } from "@/components/coverage-map";
import { Aurora, Grain, Particles, Marquee, TiltCard, spotlightPointer } from "@/components/motion";
import { pageMeta, brand, site, industries, process as steps, testimonials } from "@/content";

export const Route = createFileRoute("/")({
  head: () => {
    const head = routeHead("/", { jsonLd: organizationSchema() });
    return {
      ...head,
      links: [
        ...(head.links ?? []),
        { rel: "preload", as: "image", href: "/jamee-full.webp", fetchPriority: "high" },
      ],
    };
  },
  component: Index,
});

const PILLARS = [
  {
    n: "01",
    title: "Financial Operations",
    text: "The daily bookkeeping and reconciliation that keeps your numbers current, not caught up once a quarter.",
  },
  {
    n: "02",
    title: "Payroll and People",
    text: "Wages, STP and superannuation handled the same way, every pay cycle, without you chasing it.",
  },
  {
    n: "03",
    title: "Payables and Receivables",
    text: "Bills paid on time, invoices that go out and get followed up, so cash sits where it should.",
  },
  {
    n: "04",
    title: "Reporting and Insights",
    text: "Monthly numbers with an actual explanation attached, so you know what moved and why.",
  },
  {
    n: "05",
    title: "Systems and Controls",
    text: "Xero and the processes around it, set up properly and kept that way as the business changes.",
  },
  {
    n: "06",
    title: "Strategic Oversight",
    text: "Jamee across the file, so the finance function does not depend on any one person's memory.",
  },
];

const OUTCOMES = [
  {
    title: "Greater control, not more admin.",
    text: "You get visibility into the business without taking on more of the finance workload yourself.",
  },
  {
    title: "Numbers you can actually trust.",
    text: "Reports built on books that reconcile properly, checked by someone senior before they reach you.",
  },
  {
    title: "One less thing depending on you.",
    text: "A finance function that keeps running, and keeps pace with the business, whether or not you are the one watching it every day.",
  },
];

function heroPointer(e: ReactMouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty("--px", String(((e.clientX - r.left) / r.width - 0.5) * 2));
  el.style.setProperty("--py", String(((e.clientY - r.top) / r.height - 0.5) * 2));
}

function Index() {
  const p = pageMeta("/");
  const [featured, ...restTestimonials] = testimonials;
  const secondFeatured = restTestimonials[1];
  const supportingTestimonials = restTestimonials.filter((t) => t !== secondFeatured);

  return (
    <div className="bg-background text-foreground">
      {/* Hero: one statement, one image, no floating chips or dashboards. */}
      <section onMouseMove={heroPointer} className="relative overflow-hidden border-b border-line">
        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-16 px-6 pb-16 pt-14 md:grid-cols-[1.05fr_1fr] md:pb-20 md:pt-20">
          <div>
            <h1 className="hero-enter font-serif text-4xl leading-[1.08] text-ink sm:text-5xl lg:text-[3.75rem]">
              {p?.h1 ?? brand.headline}
            </h1>
            <p className="hero-enter-delay mt-7 max-w-lg text-lg leading-relaxed text-ink/80">
              {brand.supportingCopy}
            </p>
            <div className="hero-enter-delay mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to={site.primaryCta.href} className="btn-primary w-full sm:w-auto">
                {site.primaryCta.label}
                <Arrow className="h-4 w-4" />
              </Link>
              <a href="#the-model" className="btn-secondary w-full sm:w-auto">
                Discover NextEra
              </a>
            </div>
          </div>

          <div className="hero-enter-delay parallax-soft relative mx-auto w-full max-w-sm md:max-w-none">
            <div
              className="orbit-ring left-1/2 top-1/2 hidden aspect-square w-[112%] -translate-x-1/2 -translate-y-1/2 md:block"
              aria-hidden
            />
            <div
              className="orbit-ring orbit-ring--reverse left-1/2 top-1/2 hidden aspect-square w-[134%] -translate-x-1/2 -translate-y-1/2 md:block"
              aria-hidden
            />
            <div className="photo-frame relative">
              <img
                src="/jamee-full.webp"
                alt="Portrait of Jamee White, CPA, founder of NextEra"
                width="1200"
                height="1600"
                className="relative w-full rounded-none object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
        <div className="horizon-divider" />
      </section>

      {/* Trust ticker: a moving strip bridging the hero into the tension section. */}
      <section className="border-b border-line bg-stone/60 py-5">
        <Marquee items={industries.map((i) => i.name)} className="text-ink/70" />
      </section>

      {/* The tension: three statements, nothing else on screen. */}
      <section onMouseMove={spotlightPointer} className="spotlight horizon-field">
        <Aurora />
        <Grain />
        <Particles count={10} />
        <div className="relative z-10 mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <p className="reveal font-serif text-2xl leading-snug text-ivory/60 sm:text-3xl">
            Your business has outgrown basic bookkeeping.
          </p>
          <p className="reveal mt-10 font-serif text-3xl leading-snug text-ivory sm:text-4xl">
            It needs a finance function.
          </p>
          <p className="reveal text-shimmer mt-10 font-serif text-2xl italic leading-snug text-ivory sm:text-3xl">
            That is where NextEra comes in.
          </p>
        </div>
      </section>

      {/* The NextEra model: six connected disciplines, not commodity cards.
          Asymmetric layout (intro fixed left, pillars in a tighter 2-col
          list on the right) so this reads differently from the 4-up
          "How it runs" grid below it, instead of repeating the same
          tag/heading/grid shape twice in a row. */}
      <section id="the-model" className="border-b border-line">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div className="reveal lg:max-w-sm">
              <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
                One finance function. Six connected disciplines.
              </h2>
              <p className="mt-5 leading-relaxed text-ink/75">
                Not six separate services billed like commodities. One team running one file, with
                Jamee across the whole thing.
              </p>
            </div>

            <div className="stagger grid gap-x-10 gap-y-10 sm:grid-cols-2">
              {PILLARS.map((pillar) => (
                <TiltCard key={pillar.n} className="border-t border-line pt-6">
                  <span className="font-serif text-sm text-teal">{pillar.n}</span>
                  <h3 className="mt-3 font-serif text-xl text-ink">{pillar.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink/70">{pillar.text}</p>
                </TiltCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What changes for the client: editorial statements, not icon cards. */}
      <section className="horizon-field--light border-b border-line">
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-20">
          <div className="reveal">
          </div>
          <div className="mt-10 space-y-10">
            {OUTCOMES.map((o, idx) => (
              <div
                key={o.title}
                className={`reveal grid gap-4 sm:grid-cols-[auto_1fr] sm:gap-10 ${idx > 0 ? "border-t border-line pt-10" : ""}`}
              >
                <span className="font-serif text-2xl text-ink/25">0{idx + 1}</span>
                <div>
                  <h3 className="font-serif text-2xl text-ink sm:text-3xl">{o.title}</h3>
                  <p className="mt-3 max-w-xl leading-relaxed text-ink/75">{o.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="reveal mt-10 grid gap-4 border-t border-line pt-8 sm:grid-cols-[auto_1fr] sm:gap-10">
            <div className="font-serif text-6xl leading-none text-teal sm:text-7xl">
              <CountUp to={40} suffix="+" />
            </div>
            <div className="self-center">
              <p className="text-sm font-medium uppercase tracking-[0.14em] text-subtle">
                Years combined team experience
              </p>
              <p className="mt-2 max-w-xl leading-relaxed text-ink/75">
                Sits behind every file, with CPA oversight on every engagement and Xero partner
                accreditation across the team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How the month runs: a calm, coordinated rhythm. */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="reveal max-w-2xl">
            <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
              A coordinated monthly rhythm, not a scramble at BAS time.
            </h2>
          </div>
          <ol className="stagger mt-12 grid gap-10 md:grid-cols-4">
            {steps.map((s) => (
              <li key={s.step}>
                <TiltCard className="border-t border-line pt-6">
                  <span className="font-serif text-sm text-teal">
                    {String(s.step).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-serif text-lg text-ink">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink/70">{s.description}</p>
                </TiltCard>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Founder: editorial, not a floating badge over a stock photo. */}
      <section className="horizon-field--light border-b border-line">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 py-16 md:py-20 lg:grid-cols-[1fr_1.1fr]">
          <div className="photo-frame mx-auto w-full max-w-sm lg:mx-0">
            <img
              src="/images/jamee.webp"
              alt="Jamee White, CPA, founder of NextEra"
              width="480"
              height="480"
              className="w-full object-cover object-[center_18%]"
            />
          </div>
          <div className="reveal">
            <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
              Built around a higher standard of financial support.
            </h2>
            <p className="mt-5 max-w-xl leading-relaxed text-ink/75">
              Jamee White, CPA, founded NextEra after seeing how much a business can be held back by
              a finance function that only reacts. Her background in tax accounting, and as Head of
              Finance and Operations for a national workshop equipment distributor, shaped a simple
              standard: numbers that are current, checked and explained, not just entered.
            </p>
            <p className="mt-4 max-w-xl leading-relaxed text-ink/75">
              Jamee leads every Strategic Finance Review personally and keeps strategic oversight
              across every client file. The team runs the day to day; the standard is hers.
            </p>
            <Link
              to="/about"
              className="link-underline group mt-7 inline-flex items-center gap-1 text-sm font-medium text-teal-text hover:text-ink"
            >
              Meet the team
              <Arrow className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Client transformations: real testimonials, substantial, not a wall of cards. */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-20">
          <div className="reveal">
            <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
              What changes once someone else owns the books.
            </h2>
          </div>

          {featured && (
            <figure className="reveal mt-10 border-t border-line pt-8">
              <blockquote className="font-serif text-2xl leading-relaxed text-ink sm:text-3xl">
                &ldquo;{featured.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 text-sm">
                <span className="name-mark" aria-hidden />
                <span>
                  <span className="block font-semibold text-ink">{featured.name}</span>
                  <span className="block text-subtle">{featured.theme}</span>
                </span>
              </figcaption>
            </figure>
          )}

          {secondFeatured && (
            <figure className="reveal mt-10 border-t border-line pt-8">
              <blockquote className="font-serif text-xl leading-relaxed text-ink sm:text-2xl">
                &ldquo;{secondFeatured.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 text-sm">
                <span className="name-mark" aria-hidden />
                <span>
                  <span className="block font-semibold text-ink">{secondFeatured.name}</span>
                  <span className="block text-subtle">{secondFeatured.theme}</span>
                </span>
              </figcaption>
            </figure>
          )}

          <div className="reveal mt-10 grid gap-x-10 gap-y-8 border-t border-line pt-8 sm:grid-cols-2">
            {supportingTestimonials.map((t) => (
              <div key={t.name} className="flex gap-3">
                <span className="name-mark shrink-0" aria-hidden />
                <div>
                  <p className="text-sm leading-relaxed text-ink/80">&ldquo;{t.quote}&rdquo;</p>
                  <p className="mt-2 text-sm">
                    <span className="font-semibold text-ink">{t.name}</span>
                    <span className="text-subtle"> — {t.theme}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <Link
            to="/client-results"
            className="link-underline group mt-12 inline-flex items-center gap-1 text-sm font-medium text-teal-text hover:text-ink"
          >
            Read more client stories
            <Arrow className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* Industries: editorial rows, not identical cards. */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="reveal flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
                Financial complexity looks different in every industry we work in.
              </h2>
            </div>
            <Link
              to="/industries"
              className="link-underline group inline-flex items-center gap-1 text-sm font-medium text-teal-text hover:text-ink"
            >
              View all industries
              <Arrow className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="stagger mt-12">
            {industries.map((ind) => (
              <Link
                key={ind.href}
                to={ind.href}
                className="group flex flex-col gap-1 border-t border-line py-6 last:border-b sm:flex-row sm:items-center sm:justify-between sm:gap-10"
              >
                <span className="font-serif text-xl text-ink sm:w-64 sm:shrink-0">{ind.name}</span>
                <span className="text-sm leading-relaxed text-ink/70 sm:flex-1">{ind.summary}</span>
                <Arrow className="h-4 w-4 shrink-0 text-teal transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Team: the finance department behind the client's business. */}
      <section className="horizon-field--light border-b border-line">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="reveal max-w-2xl">
            <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
              The finance department behind your business.
            </h2>
            <p className="mt-5 max-w-xl leading-relaxed text-ink/75">
              Jamee sets the standard and stays across every file. The team runs it, every month,
              wherever your business operates.
            </p>
          </div>
          <div className="reveal mt-14 flex items-center gap-8">
            <CoverageMap className="float-y h-24 w-28 shrink-0 opacity-90" />
            <p className="max-w-sm text-sm leading-relaxed text-ink/70">
              Clients across every Australian state and territory, run by one coordinated team, not
              a patchwork of contractors.
            </p>
          </div>
          <Link
            to="/about"
            className="link-underline group mt-10 inline-flex items-center gap-1 text-sm font-medium text-teal-text hover:text-ink"
          >
            Meet the full team
            <Arrow className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* Insights: the foundation for NextEra's authority-building content. */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-20">
          <div className="reveal">
            <h2 className="mt-4 font-serif text-3xl text-ink sm:text-[2.5rem] sm:leading-[1.15]">
              A working library for owners who want their numbers working.
            </h2>
            <p className="mt-5 max-w-xl leading-relaxed text-ink/75">
              Plain-language guides on bookkeeping, BAS, payroll, Xero and cash flow, written by the
              NextEra team and checked against how the rules actually work.
            </p>
            <Link
              to="/insights"
              className="link-underline group mt-7 inline-flex items-center gap-1 text-sm font-medium text-teal-text hover:text-ink"
            >
              Explore Insights
              <Arrow className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final conversion */}
      <section onMouseMove={spotlightPointer} className="spotlight horizon-field text-ivory">
        <Aurora />
        <Grain />
        <Particles count={12} />
        <div className="relative z-10 mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
          <h2 className="reveal text-shimmer mx-auto max-w-3xl font-serif text-3xl leading-tight sm:text-[2.75rem] sm:leading-[1.15]">
            Build a finance function ready for what comes next.
          </h2>
          <p className="reveal mx-auto mt-5 max-w-2xl leading-relaxed text-ivory/75">
            A Strategic Finance Review is a proper look at your current finance function, its
            reporting, systems and controls, and where the opportunities sit. One conversation,
            no sales pitch.
          </p>
          <div className="reveal mt-9 flex flex-wrap justify-center gap-3">
            <Link to={site.primaryCta.href} className="btn-on-dark">
              {site.primaryCta.label}
              <Arrow className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
